import { useState } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useAuditLogs, type AuditLogsParams } from '@/api/audit-logs';
import { usePartnerAccountsWithPolling } from '@/api/partner-accounts';
import { useSyncStatusInvalidationForList } from '@/hooks/useSyncStatusInvalidation';
import { AuditLogList } from '@/components/audit/AuditLogList';

const ACTION_TYPES = [
  { value: 'connect', label: 'Connect' },
  { value: 'disconnect', label: 'Disconnect' },
  { value: 'reconnect', label: 'Reconnect' },
  { value: 'enable', label: 'Enable' },
  { value: 'disable', label: 'Disable' },
  { value: 'start_sync', label: 'Start Sync' },
  { value: 'pause_sync', label: 'Pause Sync' },
  { value: 'resume_sync', label: 'Resume Sync' },
  { value: 'enrich', label: 'Enrich' },
  { value: 'publish', label: 'Publish' },
  { value: 'unpublish', label: 'Unpublish' },
  { value: 'remove_image', label: 'Remove image' },
  { value: 'update_account_settings', label: 'Settings changed' },
  { value: 'ai_content_override', label: 'AI content override' },
  { value: 'regenerate_ai_content', label: 'AI content regenerated' },
  { value: 'classify_location', label: 'Classified with AI' },
  { value: 'dismiss_duplicate', label: 'Duplicate dismissed' },
  { value: 'auto_unpublish', label: 'Auto-unpublished' },
];

const ENTITY_TYPES = [
  { value: 'partner_account', label: 'Partner Account' },
  { value: 'synced_location', label: 'Location' },
];

export function AuditLogPage(): JSX.Element {
  const [params, setParams] = useState<AuditLogsParams>({
    page: 1,
    limit: 50,
  });

  const { data, isLoading } = useAuditLogs(params);

  // Watch for sync completions across all accounts and refresh audit logs automatically
  const { data: accounts } = usePartnerAccountsWithPolling();
  useSyncStatusInvalidationForList(accounts);

  const handleFilterChange = (key: keyof AuditLogsParams, value: string | undefined) => {
    setParams((prev) => ({
      ...prev,
      [key]: value,
      page: 1, // Reset page when filters change
    }));
  };

  const handlePageChange = (newPage: number) => {
    setParams((prev) => ({ ...prev, page: newPage }));
  };

  const totalPages = data?.meta.totalPages ?? 1;
  const currentPage = data?.meta.page ?? 1;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Audit Log</h1>
        <p className="text-muted-foreground">
          Track all administrative actions performed in the Partner Bridge.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Select
              value={params.actionType ?? 'all'}
              onValueChange={(value) =>
                { handleFilterChange('actionType', value === 'all' ? undefined : value); }
              }
            >
              <SelectTrigger className="w-40" data-testid="audit-action-filter">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {ACTION_TYPES.map((action) => (
                  <SelectItem key={action.value} value={action.value}>
                    {action.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={params.entityType ?? 'all'}
              onValueChange={(value) =>
                { handleFilterChange('entityType', value === 'all' ? undefined : value); }
              }
            >
              <SelectTrigger className="w-44" data-testid="audit-entity-filter">
                <SelectValue placeholder="All Entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {ENTITY_TYPES.map((entity) => (
                  <SelectItem key={entity.value} value={entity.value}>
                    {entity.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-lg">
            <span>Activity Log</span>
            {data?.meta.total !== undefined && (
              <span className="text-sm font-normal text-muted-foreground">
                {data.meta.total} entries
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <AuditLogList logs={data?.data ?? []} isLoading={isLoading} />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { handlePageChange(currentPage - 1); }}
                disabled={currentPage <= 1}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { handlePageChange(currentPage + 1); }}
                disabled={currentPage >= totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
